import api from "../api/axios";
import { Provider } from "../types/api";

export interface User {
  id: number;
  email: string;
  roleId: number;
  roleTitle: string;
  status: number;
  firstName: string;
  lastName: string;
  displayName?: string;
  phoneNumber: string;
  dateOfBirth: string;
  dentalLicenseNumber?: string;
  specialtyLicenseNumber?: string;
  specialtyIds?: number[];
  deaNumber?: string;
  npiNumber?: string;
  avatarUrl?: string;
}

export interface UserDetails extends User {}

export interface UpdateUserParams {
  id: number;
  email?: string;
  roleId?: number;
  status?: number;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  dentalLicenseNumber?: string;
  specialtyLicenseNumber?: string;
  deaNumber?: string;
  npiNumber?: string;
  specialtyIds?: number[];
}

export interface AssignedPatient {
  externalUid: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  phone: string;
  recentTreatmentPlanId?: number;
  recentTreatmentPlanName?: string;
}

export interface UnassignedPatient {
  id: number;
  fullName: string;
  dateOfBirth: string;
  gender: string;
  email: string;
  phone: string;
}

export interface GetUsersParams {
  id?: number[];
  email?: string;
  status?: number[];
  roleId?: number;
  start?: number;
  limit?: number;
  total?: boolean;
  orderBy?: string;
  order?: 0 | 1;
  search?: string;
}

export enum UserStatus {
  Unknown = 0,
  Active = 1,
  Inactive = 2,
  Invited = 3,
  Suspended = 4,
}

export enum UserRole {
  Manager = 1,
  Assistant = 2,
  Provider = 3,
}

// Specialty interface from API
export interface Specialty {
  id: number;
  name: string;
  externalId: string;
}

export interface CreateUserParams {
  email: string;
  roleId: number;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  dentalLicenseNumber?: string;
  specialtyLicenseNumber?: string;
  deaNumber?: string;
  npiNumber?: string;
  specialtyIds?: number[];
}

export const usersService = {
  /**
   * Get list of provider specialties
   */
  async getSpecialties(): Promise<Specialty[]> {
    const response = await api.get<Specialty[]>("/providers/specialties");
    return response.data;
  },

  async getProviders(): Promise<Provider[]> {
      const queryParams = new URLSearchParams();

      const providerRole = +UserRole.Provider;
      const status = UserStatus.Active;
      const limit = 1000;

      queryParams.append('roleId', providerRole.toString());
      queryParams.append('limit', limit.toString());
      queryParams.append('status', status.toString());

      const response = await api.get<User[]>(`users?${queryParams.toString()}`);
      return (response.data || []).map(x => ({
        name: `${x.firstName} ${x.lastName}`,
        id: x.id
      }));
  },

  /**
   * Get list of users with filters
   */
  async getUsers(params?: GetUsersParams): Promise<{
    data: User[];
    total: number;
  }> {
    const queryParams = new URLSearchParams();

    if (params?.id?.length) {
      params.id.forEach((id) => {
        queryParams.append("id", id.toString());
      });
    }

    if (params?.email) {
      queryParams.append("email", params.email);
    }

    if (params?.status?.length) {
      params.status.forEach((s) => {
        queryParams.append("status", s.toString());
      });
    }

    if (params?.roleId !== undefined) {
      queryParams.append("roleId", params.roleId.toString());
    }

    if (params?.start !== undefined) {
      queryParams.append("start", params.start.toString());
    }

    if (params?.limit !== undefined) {
      queryParams.append("limit", params.limit.toString());
    }

    if (params?.total !== undefined) {
      queryParams.append("total", params.total.toString());
    }

    if (params?.orderBy) {
      queryParams.append("order-by", params.orderBy);
    }

    if (params?.order !== undefined) {
      queryParams.append("order", params.order.toString());
    }

    if (params?.search) {
      queryParams.append("search", params.search);
    }

    const queryString = queryParams.toString();
    const url = queryString ? `/users?${queryString}` : "/users";
    const response = await api.get<User[]>(url);

    return {
      data: response.data || [],
      total: parseInt(response.headers["x-total-count"] as string) || 0,
    };
  },

  /**
   * Get user by ID
   */
  async getUserById(id: number): Promise<UserDetails> {
    const response = await api.get<UserDetails>(`/users/${id}`);
    return response.data;
  },

  /**
   * Suspend user (non-provider)
   */
  async suspendUser(id: number): Promise<void> {
    await api.post(`/users/${id}/suspend`);
  },

  /**
   * Suspend provider and reassign their patients to another provider
   */
  async suspendProvider(id: number, newProviderId: number): Promise<void> {
    await api.post(`/providers/${id}/suspend`, { newProviderId });
  },

  /**
   * Reactivate user
   */
  async reactivateUser(id: number): Promise<void> {
    await api.post(`/users/${id}/reactivate`);
  },

  /**
   * Resend invite to user
   */
  async resendInvite(id: number): Promise<void> {
    await api.post(`/users/${id}/invite`);
  },

  /**
   * Create new user (send invitation)
   */
  async createUser(params: CreateUserParams): Promise<User> {
    const response = await api.post<User>("/users", {
      email: params.email,
      roleId: params.roleId,
      status: UserStatus.Invited,
      firstName: params.firstName,
      lastName: params.lastName,
      phoneNumber: params.phoneNumber || null,
      dateOfBirth: params.dateOfBirth || null,
      dentalLicenseNumber: params.dentalLicenseNumber || null,
      specialtyLicenseNumber: params.specialtyLicenseNumber || null,
      deaNumber: params.deaNumber || null,
      npiNumber: params.npiNumber || null,
      specialtyIds: params.specialtyIds || [],
    });
    return response.data;
  },

  /**
   * Update user
   */
  async updateUser(params: UpdateUserParams): Promise<User> {
    const response = await api.put<User>(`/users/${params.id}`, {
      id: params.id,
      email: params.email,
      roleId: params.roleId,
      status: params.status,
      firstName: params.firstName,
      lastName: params.lastName,
      phoneNumber: params.phoneNumber || null,
      dateOfBirth: params.dateOfBirth || null,
      dentalLicenseNumber: params.dentalLicenseNumber || null,
      specialtyLicenseNumber: params.specialtyLicenseNumber || null,
      deaNumber: params.deaNumber || null,
      npiNumber: params.npiNumber || null,
      specialtyIds: params.specialtyIds || [],
    });
    return response.data;
  },

  /**
   * Upload user avatar
   */
  async uploadAvatar(userId: number, file: File): Promise<string> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post<string>(
      `/users/${userId}/avatar`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  },

  /**
   * Get assigned patients for a provider
   */
  async getAssignedPatients(
    providerId: number,
    params?: {
      search?: string;
      orderBy?: string;
      order?: 0 | 1;
      start?: number;
      limit?: number;
    },
  ): Promise<{
    data: AssignedPatient[];
    total: number;
  }> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append("search", params.search);
    if (params?.orderBy) queryParams.append("order-by", params.orderBy);
    if (params?.order !== undefined)
      queryParams.append("order", params.order.toString());
    if (params?.start !== undefined)
      queryParams.append("start", params.start.toString());
    if (params?.limit !== undefined)
      queryParams.append("limit", params.limit.toString());

    const queryString = queryParams.toString();
    const url = queryString
      ? `/users/${providerId}/patients?${queryString}`
      : `/users/${providerId}/patients`;
    const response = await api.get<AssignedPatient[]>(url);
    return {
      data: response.data || [],
      total: parseInt(response.headers["x-total-count"] as string) || 0,
    };
  },

  /**
   * Get unassigned patients for a provider
   */
  async getUnassignedPatients(
    providerId: number,
    search?: string,
  ): Promise<UnassignedPatient[]> {
    const queryParams = new URLSearchParams();
    if (search) queryParams.append("search", search);

    const queryString = queryParams.toString();
    const url = queryString
      ? `/providers/${providerId}/unassigned-patients?${queryString}`
      : `/providers/${providerId}/unassigned-patients`;
    const response = await api.get<UnassignedPatient[]>(url);
    return response.data || [];
  },

  /**
   * Assign patients to provider
   */
  async assignPatients(
    providerId: number,
    patientIds: number[],
  ): Promise<void> {
    await api.put(`/providers/${providerId}/patients`, {
      userId: providerId,
      patientIds,
    });
  },

  /**
   * Unassign patient from provider
   */
  async unassignPatient(providerId: number, patientId: number): Promise<void> {
    await api.delete(`/providers/${providerId}/patients/${patientId}`);
  },
};
